import { useEffect, type FC } from 'react'
import styled from 'styled-components'

import { Footer } from '@/components/elements/footer/Footer'
import { Container } from '@/components/shared/Container'
import { JsonLdScript } from '@/components/shared/JsonLdScript'
import { SEOMetaTags } from '@/components/shared/SEOMetaTags'
import { TrackedLink } from '@/components/shared/TrackedLink'

import { SubNavigation } from '@/elements/global/SubNavigation'
import { Header } from '@/elements/header/Header'

import type { AcademyArticleCategory } from '@/lib/api/graphql/HealthAcademyArticle'
import { trackLearnArticlePageView } from '@/lib/gtm/gtmV2'

import type { AcademyArticle, AcademyCategories } from '../AcademyContentPage'
import { FeaturedArticles } from '../FeaturedArticles/FeaturedArticles'
import { ArticleContent } from './ArticleContent'
import { ArticleHeader } from './ArticleHeader'
import { ArticleImage } from './ArticleImage'
import { ArticleNavigation } from './ArticleNavigation'

const StyledArticleWrap = styled.div`
  position: relative;
`
const StyledBreadcrumbs = styled.ul`
  padding: 48px 0 0 100px;
  margin: 0;
  list-style-type: none;
  display: flex;

  li {
    margin-right: 32px;
  }

  a {
    text-transform: uppercase;
    font-weight: 500;
    font-size: 16px;
    letter-spacing: 2.4px;
    background-image: url("data:image/svg+xml,%3Csvg width='16' height='16' viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6.15674 2.33643L0.712016 7.78115C0.594859 7.8983 0.594858 8.08825 0.712016 8.20541L6.15674 13.6501' stroke='%23445DC3' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
    background-repeat: no-repeat;
    background-position: left 5px;
    padding-left: 24px;
    padding-top: 4px;
  }
  @media screen and (max-width: ${(props) => props.theme.breakpoints.mobile}) {
    padding-left: 0;
    a {
      font-size: 14px;
    }
  }
`

type ArticleCategoryWithParent = AcademyArticleCategory & {
  parent: NonNullable<AcademyArticleCategory['parent']>
}

type ArticlePageProps = {
  article: AcademyArticle
  categories: AcademyCategories
}

export const ArticlePage: FC<ArticlePageProps> = ({ article, categories }) => {
  if (!article.category || article.category.length === 0) {
    throw new Error(
      `Article with slug '${article.articleSlug}' has no categories assigned. Please check the setup in the CMS.`,
    )
  }

  const childCategory = article.category.filter((cat): cat is ArticleCategoryWithParent => !!cat.parent)[0]
  const metaImageSrc = article.metaData?.image?.responsiveImage?.src
  const isCovid = article.category.some((category) => category.categorySlug === 'covid')
  const pageCategory = isCovid ? 'Covid' : 'Nutrition'

  useEffect(() => {
    trackLearnArticlePageView({
      articleCategory: pageCategory,
      articleTitle: article.metaData?.title ?? '',
      articleVersion: article._publishedAt,
      articleAuthor: article.author.name,
    })
  }, [])

  return (
    <>
      <JsonLdScript
        id="article-jsonld"
        data={{
          '@type': 'Article',
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${process.env.NEXT_PUBLIC_SITE_URL}/learn/${article.articleSlug}`,
          },
          headline: article.metaData?.title,
          image: metaImageSrc ? [metaImageSrc] : [],
          datePublished: article._firstPublishedAt,
          dateModified: article._publishedAt,
          author: [{ '@type': 'Person', name: article.author.name }],
          publisher: {
            '@type': 'Organization',
            name: 'ZOE',
            logo: { '@type': 'ImageObject', url: 'https://www.datocms-assets.com/46938/1619161895-zoe-logo.svg' },
          },
          description: article.metaData?.description,
        }}
      />
      {childCategory && (
        <JsonLdScript
          id="breadcrumb-jsonld"
          data={{
            '@type': 'BreadcrumbList',
            itemListElement: [
              { name: 'Learn', item: `${process.env.NEXT_PUBLIC_SITE_URL}/learn` },
              {
                name: childCategory.parent.categoryName,
                item: `${process.env.NEXT_PUBLIC_SITE_URL}/learn/category/${childCategory.parent.categorySlug}`,
              },
              {
                name: childCategory.categoryName,
                item: `${process.env.NEXT_PUBLIC_SITE_URL}/learn/category/${childCategory.parent.categorySlug}/${childCategory.categorySlug}`,
              },
              {
                name: article.articleTitle,
                item: `${process.env.NEXT_PUBLIC_SITE_URL}/learn/${article.articleSlug}`,
              },
            ].map((crumb, index) => ({ '@type': 'ListItem', position: index + 1, ...crumb })),
          }}
        />
      )}
      <SEOMetaTags metaData={article.metaData ?? undefined} type="article" />

      <Header />

      <SubNavigation
        title="Learn"
        categories={categories}
        selected={childCategory && childCategory.parent.categorySlug}
      />

      <main id="main-content">
        <article aria-label={article.articleTitle}>
          <Container>
            <StyledBreadcrumbs>
              <li>
                <TrackedLink href={`/learn/category/${childCategory.parent.categorySlug}`}>
                  {childCategory.parent.categoryName}
                </TrackedLink>
              </li>
              <li>
                <TrackedLink
                  href={`/learn/category/${childCategory.parent.categorySlug}/${childCategory.categorySlug}`}
                >
                  {childCategory.categoryName}
                </TrackedLink>
              </li>
            </StyledBreadcrumbs>
          </Container>
          <ArticleHeader article={article} />
          <ArticleImage article={article} />
          <StyledArticleWrap>
            <ArticleNavigation article={article} />
            <ArticleContent article={article} showInApp={false} />
          </StyledArticleWrap>
          {article.featuredArticles.length > 0 && (
            <FeaturedArticles articles={article.featuredArticles} showViewMoreButton={false} />
          )}
        </article>
      </main>

      <Footer showPreFooter />
    </>
  )
}
